import { Component, Inject, OnInit } from '@angular/core';
import { MODAL_DATA, NgbActiveModal } from '@agilent/ui-element';
import { Customer, CustomerApiService } from '@app/api/customer-api.service';
import { OpenLabSystem, OpenLabSystemApiService } from '@app/api/openlab-system-api.service';
import { SoftwareImage } from '@app/api/software-images-api.service';
import { FormControl, FormGroup } from '@angular/forms';
import { User } from '@app/api/user-api.service';
import { UserPrivilegeEnum, UserService } from '@app/user.service';
import * as moment from 'moment';
import Utils from '@app/utils/utils';
import { CustomValidators } from '@app/shared/custom-input-validators';

@Component({
    selector: 'app-add-customer',
    template: `
        <form id="add-customer-form" [formGroup]="customerForm">
            <ag-dialog header="{{ modalData.existingCustomer ? 'Edit Details' : 'Create Customer' }}">
                <ag-dialog-content>
                    <app-input-wrapper
                        formControlName="customerName"
                        id="customer-field"
                        label="Customer Name"
                        [required]="true"
                        [(ngModel)]="customerData.name"
                        (ngModelChange)=nameChange($event)
                        [disabled]="customerData.is_system"
                        maxlength="100">
                    </app-input-wrapper>
                    <div *ngIf="showNameExistsError && createCustomerErrorValue === 'customer_exists'"
                         class="error-label text-danger form-error-message move-up">
                        <span>{{'Customer name is already in use'}}</span>
                    </div>

                    <app-input-wrapper
                        formControlName="account_id_ett"
                        id="account_id_ett-field"
                        [required]="true"
                        label="Account ID (ETT)"
                        labelPosition="top"
                        [(ngModel)]="customerData.account_id_ett"
                        *ngIf=canEditAgilentOnlyFields
                        maxlength="100"
                        [disabled]="isLoading">
                    </app-input-wrapper>

                    <app-input-wrapper
                        formControlName="system_handle_ett"
                        id="system_handle_ett-field"
                        label="System Handle (ETT)"
                        labelPosition="top"
                        [(ngModel)]="customerData.system_handle_ett"
                        *ngIf=canEditAgilentOnlyFields
                        maxlength="100"
                        [required]="true"
                        [disabled]="isLoading">
                    </app-input-wrapper>

                    <app-input-wrapper
                        formControlName="customer_name_ett"
                        id="customer_name_ett-field"
                        label="Customer Name (ETT)"
                        labelPosition="top"
                        [(ngModel)]="customerData.customer_name_ett"
                        *ngIf=canEditAgilentOnlyFields
                        maxlength="100"
                        [required]="true"
                        [disabled]="isLoading">
                    </app-input-wrapper>

                    <app-input-wrapper
                        formControlName="location_ett"
                        id="location_ett-field"
                        label="Location (ETT)"
                        labelPosition="top"
                        [(ngModel)]="customerData.location_ett"
                        *ngIf=canEditAgilentOnlyFields
                        maxlength="100"
                        [disabled]="isLoading">
                    </app-input-wrapper>

                    <div class="dates" *ngIf=canEditAgilentOnlyFields>
                        <ag-datepicker
                            id="purchase_date-field"
                            formControlName="purchase_date"
                            [(ngModel)]="purchaseDate"
                            label="Purchase Date"
                            labelPosition="top"
                            (onDateChange)="onSelectPurchaseDate($event)"
                            [disabled]="isLoading">
                        </ag-datepicker>
                        <ag-datepicker
                            id="sma_expire_date-field"
                            formControlName="sma_expire_date"
                            [(ngModel)]="smaExpireDate"
                            label="SMA Expiry Date"
                            labelPosition="top"
                            (onDateChange)="onSelectSMAExpiryDate($event)"
                            [disabled]="isLoading">
                        </ag-datepicker>
                    </div>

                    <ag-input
                        formControlName="address_1"
                        id="address_1-field"
                        label="Address Line 1"
                        labelPosition="top"
                        [(ngModel)]="customerData.address_1"
                        maxlength="100"
                        [disabled]="isLoading">
                    </ag-input>

                    <ag-input
                        formControlName="address_2"
                        id="address_2-field"
                        label="Address Line 2"
                        labelPosition="top"
                        [(ngModel)]="customerData.address_2"
                        maxlength="100"
                        [disabled]="isLoading">
                    </ag-input>

                    <ag-input
                        formControlName="country"
                        id="country-field"
                        label="Country"
                        labelPosition="top"
                        [(ngModel)]="customerData.country"
                        maxlength="100"
                        [disabled]="isLoading">
                    </ag-input>

                    <ag-input
                        formControlName="phone"
                        id="phone-field"
                        label="Phone (e.g 408-235-4333)"
                        labelPosition="top"
                        [(ngModel)]="customerData.phone"
                        maxlength="100"
                        [disabled]="isLoading">
                    </ag-input>

                    <ag-input
                        formControlName="primary_contact"
                        id="primary_contact-field"
                        label="Primary Contact"
                        labelPosition="top"
                        [(ngModel)]="customerData.primary_contact"
                        maxlength="100"
                        [disabled]="isLoading">
                    </ag-input>

                    <ag-input
                        formControlName="secondary_contact"
                        id="secondary_contact-field"
                        label="Secondary Contact"
                        labelPosition="top"
                        [(ngModel)]="customerData.secondary_contact"
                        maxlength="100"
                        [disabled]="isLoading">
                    </ag-input>

                    <ag-input
                        formControlName="comment"
                        id="comment-field"
                        label="Comments"
                        labelPosition="top"
                        [(ngModel)]="customerData.comment"
                        maxlength="250"
                        valueType="multiline"
                        [disabled]="isLoading">
                    </ag-input>

                    <app-user-reason-form
                        *ngIf="userReasonRequired"
                        id="reason-field"
                        label="Reason/description for this change"
                        [required]="true"
                        [disabled]="isLoading"
                        (formReady)="formIsReady($event)"
                        maxlength="255">
                    </app-user-reason-form>
                    <div
                        *ngIf="openLabVersionsDoNotMatch"
                        class="error-label text-danger form-error-message">
                        Please select an AIC software image and default OpenLab Server that have the same OpenLab
                        version (2.4 or 2.6).
                    </div>
                </ag-dialog-content>
                <ag-dialog-actions>
                    <span *ngIf="isLoading">Please wait...</span>
                    <ag-loader id="loading-spinner" class="inline-progress"
                               [visible]="isLoading " [small]="true"></ag-loader>
                    <ag-button
                        id="cancel-btn"
                        type="secondary"
                        (click)="cancel()"
                        value="Cancel"></ag-button>
                    <ag-button
                        id="save-btn"
                        type="primary"
                        (click)="saveCustomer()"
                        [disabled]="isAddButtonDisabled()"
                        [class.disabled]="isAddButtonDisabled()"
                        [value]="addUpdateBtnLabel">
                    </ag-button>
                </ag-dialog-actions>
            </ag-dialog>
        </form>
    `,
    styles: [`
        :host ::ng-deep ag-label, .label {
            width: 20%;
        }

        :host .inline-progress > ::ng-deep .loader-overlay {
            background: transparent !important;
            margin: 0;
            padding: 0;
            position: static;
            transform: scale(0.7);
        }

        .form-error-message {
            font-size: .875rem;
            line-height: 1rem;
            float: left;
        }

        .move-up {
            margin-top: -25px;
            margin-bottom: 20px;
        }

        .dates {
            display: inline-flex;
        }
    `, 'ag-select, ag-input {margin: 1rem 0;}'
    ]
})
export class AddCustomerComponent implements OnInit {

    customerData: Customer = {
        id: 0,
        is_deleted: false,
        is_system: false,
        name: '',
        address_1: '',
        address_2: '',
        country: '',
        phone: '',
        purchase_date: '',
        sma_expire_date: '',
        primary_contact: '',
        secondary_contact: '',
        comment: '',
        default_aic_version_name: null,
        ac_count: 0,
        user_count: 0,
        server_count: 0,
        account_id_ett: '',
        system_handle_ett: '',
        customer_name_ett: '',
        location_ett: '',
    };
    addUpdateBtnLabel = 'Add';
    isLoading = false;
    openLabSystems: OpenLabSystem[] = [];
    openLabSystemNames = [];
    isDefaultOlsEnabled = false;
    openLabSystemName = null;
    aicSoftwareImages: SoftwareImage[] = [];
    aicSoftwareImageVersionNames = [];
    defaultAicSoftwareImageVersionName = null;
    customerForm: FormGroup;
    openLabVersionsDoNotMatch = false;
    createCustomerErrorValue: string;
    showNameExistsError = false;
    submittedExistingCustomerName: string;
    authUser: User;
    purchaseDate: Date;
    smaExpireDate: Date;
    userReasonRequired = false;
    canEditAgilentOnlyFields = false;

    constructor(
        private activeModal: NgbActiveModal,
        private customerApiService: CustomerApiService,
        private openLabSystemApiService: OpenLabSystemApiService,
        private userService: UserService,
        @Inject(MODAL_DATA) private modalData: any
    ) {
        this.userReasonRequired = modalData.userReasonRequired || false;

        // First check if we have data we need to edit
        if (modalData.existingCustomer) {
            // Make a copy of the existing data to be modified
            this.customerData = Object.assign({}, modalData.existingCustomer);
            this.isDefaultOlsEnabled = (this.customerData.default_openlab_system_id != null);

            // Change the label on the button
            this.addUpdateBtnLabel = 'Update';

            this.getOpenLabSystems();

        }

        this.getAicSoftwareImages();
    }

    async ngOnInit() {
        this.purchaseDate = this.customerData.purchase_date &&
            moment.parseZone(this.customerData.purchase_date).local().toDate();
        this.smaExpireDate = this.customerData.sma_expire_date &&
            moment.parseZone(this.customerData.sma_expire_date).local().toDate();

        this.customerForm = new FormGroup({
            customerName: new FormControl(this.customerData.name, [
                CustomValidators.blankField('Customer name')]),
            account_id_ett: new FormControl(this.customerData.account_id_ett, [CustomValidators.blankField('Account ID (ETT)')]),
            system_handle_ett: new FormControl(this.customerData.system_handle_ett, [CustomValidators.blankField('System handle (ETT)')]),
            customer_name_ett: new FormControl(this.customerData.customer_name_ett, [CustomValidators.blankField('Customer name (ETT)')]),
            location_ett: new FormControl(this.customerData.location_ett),
            address_1: new FormControl(this.customerData.address_1),
            address_2: new FormControl(this.customerData.address_2),
            country: new FormControl(this.customerData.country),
            phone: new FormControl(this.customerData.phone),
            purchase_date: new FormControl(this.purchaseDate),
            sma_expire_date: new FormControl(this.smaExpireDate),
            primary_contact: new FormControl(this.customerData.primary_contact),
            secondary_contact: new FormControl(this.customerData.secondary_contact),
            comment: new FormControl(this.customerData.comment),
            openLabSystem: new FormControl(this.openLabSystemName),
            defaultAicSoftwareImageVersionName: new FormControl(this.defaultAicSoftwareImageVersionName),
            reason: new FormControl('')
        });
        this.authUser = await this.userService.getAuthUser();

        this.canEditAgilentOnlyFields = await this.userService.userHasPrivs(
            [UserPrivilegeEnum.EDIT_AGILENT_ONLY_FIELDS], this.authUser.customer_id);
    }

    /**
     * Calls the api to save the new customer data on the server
     */
    async saveCustomer() {
        // Validate the form. We do this here because Angular doesn't always
        // run the form validation function after the user changes their
        // selection in the drop down menus (ag-selects). This means we
        // only validate when the user attempts to save.
        const formErrors = this.validateForm();
        this.openLabVersionsDoNotMatch = (
            formErrors && formErrors.openLabVersionsDoNotMatch);
        if (this.openLabVersionsDoNotMatch) {
            return;
        }

        // show the loader
        this.isLoading = true;
        try {
            // Set the default_aic_version_name field
            this.customerData.default_aic_version_name = this.defaultAicSoftwareImageVersionName;
            this.createCustomerErrorValue = null;

            // remove these fields since we do not save them on our customer table but
            // we store them so it won't affect the customer list counts on the page
            const ac_count = this.customerData.ac_count;
            const user_count = this.customerData.user_count;
            const server_count = this.customerData.server_count;
            delete this.customerData.ac_count;
            delete this.customerData.user_count;
            delete this.customerData.server_count;

            this.customerData.purchase_date = this.purchaseDate ? this.purchaseDate.toISOString() : null;
            this.customerData.sma_expire_date = this.smaExpireDate ? this.smaExpireDate.toISOString() : null;
            // Trim whitespace except password
            Utils.trimObjectValues(this.customerData, ['password']);

            let customer = null;
            let customerSavedMessage: string;
            if (this.modalData.existingCustomer) {
                // We are updating an existing customer

                // Set the default_openlab_system_id field
                delete this.customerData.default_openlab_system;
                this.customerData.default_openlab_system_id = null;
                if (this.isDefaultOlsEnabled) {
                    for (const ols of this.openLabSystems) {
                        if (ols.name === this.openLabSystemName) {
                            this.customerData.default_openlab_system_id = ols.id;
                        }
                    }
                }
                const updateResp = await this.customerApiService.updateCustomer({
                    customer: this.customerData,
                    reason: this.customerForm.get('reason').value
                });
                customer = updateResp.customer;
                customerSavedMessage = 'Update successful';
            }
            else {
                // We are creating an entirely new customer
                const createResp = await this.customerApiService.createCustomer({
                    customer: this.customerData
                });
                customer = createResp.customer;
                customerSavedMessage = 'Customer added';
            }
            // we add back the fields that we saved prior to the API call
            // to preserve the counts for this customer
            customer.ac_count = ac_count;
            customer.user_count = user_count;
            customer.server_count = server_count;
            // Pass the value returned from the server back to the UI
            this.activeModal.close({ customer: customer, message: customerSavedMessage });
        }
        catch (ex) {
            console.error(ex);
            this.isLoading = false;
            this.createCustomerErrorValue = ex.data && ex.data.error;
            if (this.createCustomerErrorValue == 'customer_exists') {
                this.showNameExistsError = true;
                this.submittedExistingCustomerName = this.customerData.name;
            }
            else {
                this.activeModal.dismiss(this.createCustomerErrorValue);
            }
        }
    }

    /**
     * Hides the modal
     */
    cancel() {
        this.activeModal.dismiss();
    }

    private getOpenLabSystems() {
        this.isLoading = true;
        this.openLabSystemApiService.getOpenLabSystems({
            limit: 1000,
            offset: 0,
            customerId: this.modalData.existingCustomer.id
        }).then(res => {
            this.openLabSystems = res.openlab_systems;
            const openLabSystemNames = [];
            for (const ols of this.openLabSystems) {
                openLabSystemNames.push(ols.name);
                if (ols.id === this.customerData.default_openlab_system_id) {
                    this.openLabSystemName = ols.name;
                }
            }
            this.openLabSystemNames = openLabSystemNames;
            this.isLoading = false;
        }).catch(err => {
            this.isLoading = false;
            console.error(err);
        });
    }

    private async getAicSoftwareImages() {
        try {
            this.aicSoftwareImages = [];
            this.aicSoftwareImageVersionNames = [];
            for (const image of this.aicSoftwareImages) {
                if (image.version_name === this.customerData.default_aic_version_name) {
                    this.defaultAicSoftwareImageVersionName = image.version_name;
                }
                this.aicSoftwareImageVersionNames.push(image.version_name);
            }

            // If the customer does not have a default AIC software
            // image version name, assign the most recent one from the
            // aicSoftwareImageVersionNames list
            if (this.defaultAicSoftwareImageVersionName == null) {
                this.defaultAicSoftwareImageVersionName =
                    this.aicSoftwareImageVersionNames[
                    this.aicSoftwareImageVersionNames.length - 1];
            }
        }
        catch (error) {
            console.error(error);
        }
        finally {
            this.isLoading = false;
        }
    }

    /**
     * Validates the form. Verifies that the default AIC software image
     * and the default OpenLab system have the same OpenLab version.
     */
    validateForm() {
        let selectedAicSoftwareImage = null;
        for (const aicImage of this.aicSoftwareImages) {
            if (aicImage.version_name === this.defaultAicSoftwareImageVersionName) {
                selectedAicSoftwareImage = aicImage;
                break;
            }
        }

        let selectedOpenLabSystem = null;
        for (const openLabSystem of this.openLabSystems) {
            if (openLabSystem.name === this.openLabSystemName) {
                selectedOpenLabSystem = openLabSystem;
                break;
            }
        }

        if (selectedAicSoftwareImage == null) {
            return null;
        }
        if (selectedOpenLabSystem == null) {
            return null;
        }
        if (selectedAicSoftwareImage.openlab_version !== selectedOpenLabSystem.openlab_version) {
            return { openLabVersionsDoNotMatch: true };
        }

        return null;
    }

    getFormErrors(form, fieldName) {
        return (
            form.touched &&
            form.controls[fieldName] &&
            form.controls[fieldName].errors
        ) || {};
    }

    nameChange(enteredName) {
        // if the currently typed matches the recently submitted name that already
        // exists, then display the error message
        if (this.submittedExistingCustomerName &&
            (this.submittedExistingCustomerName.toLowerCase() == enteredName.toLowerCase())) {
            this.showNameExistsError = true;
        }
        else {
            this.showNameExistsError = false;
        }
    }

    onSelectPurchaseDate(date) {
        // if we clear everything from the date input, clear the form error too
        if (!date.newDate) {
            this.customerForm.controls['purchase_date'].setErrors(null);
        }
    }

    onSelectSMAExpiryDate(date) {
        // if we clear everything from the date input, clear the form error too
        if (!date.newDate) {
            this.customerForm.controls['sma_expire_date'].setErrors(null);
        }
    }

    private isOnlySpaces(word: string) {
        return Utils.isOnlySpaces(word);
    }

    formIsReady(reasonForm: FormGroup) {
        this.customerForm.setControl('reason', reasonForm.get('userReason'));
    }

    isAddButtonDisabled() {
        return (
            !this.customerForm.dirty ||
            this.customerForm.invalid
        );
    }
}
